import styled from '@emotion/styled';
import React, { FC, useEffect, useState } from 'react';
import { useCart } from '~Components/CartProvider';
import LoyaltyModal, { ContactlessIcon } from '~Components/LoyaltyModal';
import { useStorage } from '~Components/Storage';
import { useButton, useI18n, useLoyalty } from '~Hooks';
import { BlackButton, GreenButton } from '~Styles';
import { UnderTitle } from '../styles';

const Button = styled(BlackButton)`
  height: 56px;
  width: 152px;
  padding: 4px;
  position: relative;
  display: flex;
  flex-direction: column;
`;

/** Green once a member is attached, so the till shows it without being read. */
const IdentifiedButton = styled(GreenButton)`
  height: 56px;
  width: 152px;
  padding: 4px;
  position: relative;
  display: flex;
  flex-direction: column;
`;

/** White here - the green background makes the default muted color hard to read. */
const IdentifiedUnderTitle = styled(UnderTitle)`
  color: var(--colorWhite);
`;

/** Anchors the waiting pulse over the Loyalty button. */
const LoyaltySlot = styled.div`
  position: relative;
  display: flex;
`;

/**
 * Sits above the button while the terminal waits for a tap, so the wait is
 * visible from across the counter without holding a modal open.
 */
const WaitingForCard = styled.div`
  position: absolute;
  bottom: calc(100% + 8px);
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  align-items: center;
  justify-content: center;
  width: 46px;
  height: 46px;
  border-radius: 50%;
  background: #12321f;
  border: 1px solid rgba(23, 201, 100, 0.4);
  color: #17c964;
  pointer-events: none;
`;

const LoyaltyButton: FC = () => {
  const { i18n } = useI18n();
  const cart = useCart();
  const loyalty = useLoyalty();
  const [storage] = useStorage();
  const terminalId = storage.adyenTerminals[0]?.POIID;

  /**
   * The name is normally already on the cart, set the instant a member is
   * attached - so marking the button doesn't wait on a refetch. Only missing
   * it when a member id landed on the cart some other way (e.g. a restored
   * parked order), in which case it's fetched here as a fallback.
   */
  const [loyaltyMemberName, setLoyaltyMemberName] = useState<string | null>(cart.loyaltyUserName);

  const loyaltyButton = useButton({
    onClick: loyalty.loyaltyModal.show,
    disabled: loyalty.isWaitingForCard,
  });

  useEffect(() => {
    if (!cart.loyaltyUserId) {
      setLoyaltyMemberName(null);
      return;
    }

    // Already known - set the moment the member was attached. Nothing to fetch.
    if (cart.loyaltyUserName) {
      setLoyaltyMemberName(cart.loyaltyUserName);
      return;
    }

    let cancelled = false;

    loyalty
      .getMember(cart.loyaltyUserId)
      .then((member) => {
        if (!cancelled) {
          setLoyaltyMemberName(member.name);
          // Backfills the cart so the next mount is instant too.
          cart.setLoyaltyUserName(member.name);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setLoyaltyMemberName(null);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [cart.loyaltyUserId, cart.loyaltyUserName]);

  return (
    <>
      <LoyaltySlot>
        {loyalty.isWaitingForCard && (
          <WaitingForCard title={i18n('Loyalty.WaitingForCard')}>
            <ContactlessIcon size={30} animated />
          </WaitingForCard>
        )}
        {loyaltyMemberName ? (
          <IdentifiedButton {...loyaltyButton}>
            {i18n('Loyalty.Title').toUpperCase()}
            <IdentifiedUnderTitle>{loyaltyMemberName}</IdentifiedUnderTitle>
          </IdentifiedButton>
        ) : (
          <Button {...loyaltyButton}>{i18n('Loyalty.Title').toUpperCase()}</Button>
        )}
      </LoyaltySlot>

      <LoyaltyModal
        isShowing={loyalty.loyaltyModal.isShowing}
        hide={loyalty.loyaltyModal.hide}
        terminalId={terminalId}
        pendingCard={loyalty.pendingCard}
        pendingError={loyalty.pendingError}
        onRequestTapCard={() => loyalty.waitForCard(terminalId)}
        onClearPending={loyalty.clearPending}
      />
    </>
  );
};

export default LoyaltyButton;
